/**
 * PreviewExternalAccountant — pixel-faithful render of the ClaimSync "External
 * Accountant Portal" template (Daylight variant supplied), with a light/dark
 * toggle.
 *
 * The portal is the focused workbench an outside CPA uses to review and sign off
 * on assigned forensic engagements. The sidebar engagement list + the header
 * breadcrumb subject + the reconstructed-indemnity totals are wired to a REAL
 * claim (from /api/v1/reports) where the data maps. The engine classification
 * table, methodology note, acknowledgments, and the electronic-signature block
 * have no live source for an outside attester (they're attestation internals
 * gated behind the engagement workflow), so they use the template's
 * representative static content — exactly as PreviewClientPortal does. This is a
 * design preview, not the live attester surface.
 *
 * Mounted at /preview/external-accountant. Self-contained — touches no real
 * route, flag, or working surface. Black + orange discipline; green ("done")
 * only on supported engines and completed acknowledgments.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF,
  BODY,
  MONO,
  PREVIEW_ZOOM,
  Palette,
  ChipKind,
  useTheme,
  ThemeToggle,
  chipStyle,
  fmtUSD,
  relTime,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  status?: string;
  result?: { final_claim_amount?: number };
  created_at?: string;
  updated_at?: string;
}

// ---- representative content (template) ----
interface NavItem {
  id: string;
  label: string;
  icon: string;
  badge?: string;
}

const NAV: NavItem[] = [
  { id: 'home', label: 'Home', icon: '◧' },
  { id: 'attest', label: 'Attestations', icon: '✺', badge: '2' },
  { id: 'reports', label: 'Reports', icon: '▤' },
  { id: 'comments', label: 'Comments', icon: '◴' },
  { id: 'profile', label: 'Profile', icon: '◵' },
];

interface EngineRow {
  name: string;
  output: string;
  method: string;
  status: string;
  kind: ChipKind;
}

const ENGINES: EngineRow[] = [
  { name: 'RCV / ACV valuation', output: '$269,066', method: 'Age-life', status: 'Supported', kind: 'green' },
  { name: 'But-for revenue', output: '$2.41M', method: 'Prophet', status: 'Supported', kind: 'green' },
  { name: 'BI loss (net)', output: '$312,000', method: 'Var-profit', status: 'Contested', kind: 'orange' },
  { name: 'Continuation rate', output: '62%', method: 'Continuation', status: 'Conditional', kind: 'neutral' },
  { name: 'Monte Carlo', output: 'P50 $585k', method: '10k-iter', status: 'Supported', kind: 'green' },
  { name: 'Present value', output: '$568,900', method: 'Discounted', status: 'Supported', kind: 'green' },
];

interface Ack {
  label: string;
  checked: boolean;
}

const ACKS: Ack[] = [
  { label: 'I reviewed the reconstructed indemnity and supporting engines.', checked: true },
  { label: 'The methodology is appropriate and consistently applied.', checked: true },
  { label: 'I noted the 1 contested item (BI continuation rate).', checked: true },
  { label: 'This attestation complies with ISO CP 04 11.', checked: false },
];

// status → engagement chip kind/label for the sidebar list
function engagementChip(status?: string): { kind: ChipKind; label: string } {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'complete' || s === 'finalized' || s === 'signed')
    return { kind: 'green', label: 'Signed off' };
  if (s === 'draft') return { kind: 'ghost', label: 'Draft' };
  return { kind: 'orange', label: 'Awaiting' };
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || 'RK'
  );
}

export default function PreviewExternalAccountant() {
  const { theme, P, toggle } = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ApiReport[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports)
          ? data.reports
          : Array.isArray(data)
            ? data
            : [];
        const sorted = [...list].sort(
          (a, b) =>
            new Date(b.updated_at || b.created_at || 0).getTime() -
            new Date(a.updated_at || a.created_at || 0).getTime(),
        );
        if (alive) setReports(sorted);
      } catch {
        if (alive) setReports([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // the engagement under review = most-recently-updated real claim
  const subject = reports[0] || null;
  const subjectName = subject?.client_name || 'Riverside Kitchen & Tap';
  const subjectClaimNo = subject ? `Claim ${(subject.id || '').slice(0, 8).toUpperCase()}` : 'Claim HFS-TX-2023-88214';

  // header attestation chip reflects the subject's status
  const subjectChip = engagementChip(subject?.status);

  // sidebar "assigned engagements" — real reports where they map, capped to keep
  // the rail compact like the template
  const engagements = useMemo(() => reports.slice(0, 6), [reports]);

  // reconstructed-indemnity total demand from the real claim where present
  const totalDemand = subject?.result?.final_claim_amount;

  const navItemStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    padding: '9px 18px 9px 19px',
    fontSize: 13.5,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'none',
    borderLeft: `3px solid ${active ? P.orange : 'transparent'}`,
    color: active ? P.textPrimary : P.textLabel,
    background: active ? P.elevBg : 'transparent',
  });

  return (
    <div
      style={{
        zoom: PREVIEW_ZOOM,
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        background: P.pageBg,
        fontFamily: BODY,
        color: P.textBody,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Static preview banner */}
      <div style={{ background: P.orange, color: '#1a1a1a', padding: '10px 20px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '1px', border: '1.5px solid #1a1a1a', borderRadius: 4, padding: '2px 8px', fontSize: 10.5 }}>Static design preview</span>
        <span>These numbers are NOT live.</span>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ============ REDUCED EXTERNAL SIDEBAR ============ */}
      <aside
        style={{
          width: 230,
          flex: '0 0 230px',
          background: P.pageBg,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRight: `1px solid ${P.railHairline}`,
        }}
      >
        <div style={{ padding: '20px 20px 18px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: P.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 30px',
            }}
          >
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
            <div
              style={{
                fontSize: 9.5,
                letterSpacing: '1.4px',
                color: P.textMuted,
                textTransform: 'uppercase',
                marginTop: 2,
              }}
            >
              External Attester
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '8px 0', display: 'flex', flexDirection: 'column' }}>
          <div>
            {NAV.map((item) => (
              <a key={item.id} style={navItemStyle(item.id === 'attest')}>
                <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.badge && (
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      background: P.orange,
                      color: P.onOrange,
                      borderRadius: 999,
                      padding: '1px 7px',
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                  >
                    {item.badge}
                  </span>
                )}
              </a>
            ))}
          </div>

          {/* assigned engagements — wired to real reports */}
          {engagements.length > 0 && (
            <div style={{ marginTop: 18, padding: '0 16px' }}>
              <div
                style={{
                  fontSize: 9.5,
                  letterSpacing: '1.4px',
                  color: P.textMuted,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  margin: '0 4px 8px',
                }}
              >
                Assigned engagements
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {engagements.map((r) => {
                  const c = engagementChip(r.status);
                  return (
                    <div
                      key={r.id}
                      onClick={() => navigate(`/report/${r.id}`)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: 9,
                        border: `1px solid ${P.cardBorder}`,
                        background: P.cardBg,
                        padding: '9px 11px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            color: P.textPrimary,
                            fontWeight: 500,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.client_name || 'Untitled claim'}
                        </div>
                        <span style={{ ...chipStyle(c.kind, P), fontSize: 9.5, padding: '1px 7px' }}>{c.label}</span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: 4,
                        }}
                      >
                        <span style={{ fontSize: 10, color: P.textMuted, fontFamily: MONO }}>
                          {(r.id || '').slice(0, 8).toUpperCase()}
                        </span>
                        <span style={{ fontSize: 10, color: P.textMuted }}>
                          {relTime(r.updated_at || r.created_at)}
                        </span>
                      </div>
                      {typeof r.result?.final_claim_amount === 'number' && (
                        <div style={{ fontSize: 11, color: P.orangeText, fontFamily: MONO, marginTop: 3 }}>
                          {fmtUSD(r.result.final_claim_amount)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        <div
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${P.railHairline}`,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: P.elevBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: P.textBody,
              fontWeight: 600,
              fontSize: 12.5,
              flex: '0 0 32px',
            }}
          >
            PN
          </div>
          <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Priya Nair, CPA</div>
            <div style={{ color: P.textLabel, fontSize: 11 }}>Lic. TX-0429183</div>
          </div>
          <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        <header
          style={{
            height: 58,
            flex: '0 0 58px',
            background: P.pageBg,
            borderBottom: `1px solid ${P.railHairline}`,
            display: 'flex',
            alignItems: 'center',
            padding: '0 26px',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: P.textLabel }}>
            <span>Attestations</span>
            <span style={{ color: P.accentNeutral }}>›</span>
            <span style={{ color: P.textPrimary, fontWeight: 500 }}>{subjectName}</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: P.orangeTintBg,
                border: `1px solid ${P.orangeTintBd}`,
                borderRadius: 999,
                padding: '5px 13px',
                fontSize: 12,
                color: P.orangeText,
                fontWeight: 600,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />
              CP 04 11 · {subjectChip.label.toLowerCase()}
            </div>
            <ThemeToggle P={P} theme={theme} onToggle={toggle} />
          </div>
        </header>

        <main style={{ flex: 1, overflow: 'auto', background: P.pageBg }}>
          <div style={{ padding: '26px 32px 48px', maxWidth: 1280, margin: '0 auto' }}>
            <div style={{ marginBottom: 22 }}>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: '1.4px',
                  color: P.orangeText,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 7,
                }}
              >
                Independent Forensic Attestation
              </div>
              <h1 style={{ fontFamily: SERIF, fontSize: 30, color: P.textPrimary, margin: 0, fontWeight: 400 }}>
                Engine Review &amp; Sign-Off
              </h1>
              <p style={{ margin: '7px 0 0', color: P.textLabel, fontSize: 14.5, maxWidth: 680 }}>
                Review the reconstructed indemnity and supporting engines for{' '}
                <strong style={{ color: P.textPrimary }}>{subjectName}</strong>. Your signature attests, under CP 04 11,
                that the methodology is appropriate and the figures are supported.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20, alignItems: 'start' }}>
              {/* REVIEW SUMMARY */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, padding: 22 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400 }}>
                      Reconstructed Indemnity
                    </h2>
                    <span style={{ fontSize: 11, color: P.textMuted, fontFamily: MONO }}>{subjectClaimNo}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                    <div style={{ borderLeft: `3px solid ${P.accentNeutral}`, padding: '4px 0 4px 14px' }}>
                      <div style={{ fontSize: 11.5, color: P.textLabel }}>Property (RCV)</div>
                      <div style={{ fontFamily: MONO, fontSize: 21, color: P.textPrimary, marginTop: 4 }}>$269,066</div>
                    </div>
                    <div style={{ borderLeft: `3px solid ${P.accentNeutral}`, padding: '4px 0 4px 14px' }}>
                      <div style={{ fontSize: 11.5, color: P.textLabel }}>Business interruption</div>
                      <div style={{ fontFamily: MONO, fontSize: 21, color: P.textPrimary, marginTop: 4 }}>$312,000</div>
                    </div>
                    <div style={{ borderLeft: `3px solid ${P.accentNeutral}`, padding: '4px 0 4px 14px' }}>
                      <div style={{ fontSize: 11.5, color: P.textLabel }}>Total demand</div>
                      <div style={{ fontFamily: MONO, fontSize: 21, color: P.orangeText, marginTop: 4 }}>
                        {typeof totalDemand === 'number' ? fmtUSD(totalDemand) : '$581,066'}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div
                    style={{
                      padding: '16px 20px',
                      borderBottom: `1px solid ${P.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <h2 style={{ fontFamily: SERIF, fontSize: 17, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>
                      Engine classification
                    </h2>
                    <span style={chipStyle('green', P)}>12 Supported</span>
                    <span style={chipStyle('neutral', P)}>1 Conditional</span>
                    <span style={chipStyle('orange', P)}>1 Contested</span>
                  </div>
                  {ENGINES.map((e) => (
                    <div
                      key={e.name}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.6fr 1fr 1fr 0.9fr',
                        padding: '12px 20px',
                        borderBottom: `1px solid ${P.rowDivider}`,
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>{e.name}</span>
                      <span style={{ fontFamily: MONO, fontSize: 12.5, color: P.textBody, textAlign: 'right' }}>{e.output}</span>
                      <span style={{ fontSize: 12, color: P.textLabel, textAlign: 'right' }}>{e.method}</span>
                      <span style={{ textAlign: 'right' }}>
                        <span style={chipStyle(e.kind, P)}>{e.status}</span>
                      </span>
                    </div>
                  ))}
                  <div style={{ padding: '13px 20px', fontSize: 12.5, color: P.orangeText, fontWeight: 600, cursor: 'pointer' }}>
                    View full Engine Audit (14) →
                  </div>
                </div>

                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, padding: 20 }}>
                  <h2 style={{ fontFamily: SERIF, fontSize: 17, color: P.textPrimary, margin: '0 0 6px', fontWeight: 400 }}>
                    Methodology
                  </h2>
                  <p style={{ fontSize: 13.5, color: P.textBody, lineHeight: 1.55, margin: 0 }}>
                    Variable-profit reconstruction, Daubert-supported and consistently applied across the benchmark and
                    claim periods. The single <strong style={{ color: P.orangeText }}>contested</strong> item — the 62%
                    expense continuation rate — is disputed by the carrier's adjuster and documented in the case record.
                  </p>
                </div>
              </div>

              {/* ATTESTATION PANEL */}
              <div
                style={{
                  background: P.cardBg,
                  border: `1px solid ${P.orangeTintBd}`,
                  borderRadius: 14,
                  overflow: 'hidden',
                  position: 'sticky',
                  top: 0,
                }}
              >
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: `1px solid ${P.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: P.orangeRowTint,
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: P.orange,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15,
                      color: P.onOrange,
                    }}
                  >
                    ✺
                  </div>
                  <div>
                    <div style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary }}>Attestation</div>
                    <div style={{ fontSize: 11, color: P.textLabel }}>CP 04 11 sign-off gate</div>
                  </div>
                </div>
                <div style={{ padding: '18px 20px' }}>
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      color: P.textMuted,
                      fontWeight: 600,
                      marginBottom: 12,
                    }}
                  >
                    Acknowledgments
                  </div>
                  {ACKS.map((a) => (
                    <div key={a.label} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '8px 0' }}>
                      <span
                        style={
                          a.checked
                            ? {
                                width: 20,
                                height: 20,
                                borderRadius: 5,
                                background: P.orange,
                                color: P.onOrange,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 700,
                                flex: '0 0 20px',
                              }
                            : {
                                width: 20,
                                height: 20,
                                borderRadius: 5,
                                background: P.pageBg,
                                border: `1.5px solid ${P.accentNeutral}`,
                                flex: '0 0 20px',
                              }
                        }
                      >
                        {a.checked ? '✓' : ''}
                      </span>
                      <span style={{ flex: 1, fontSize: 12.5, color: P.textBody, lineHeight: 1.4 }}>{a.label}</span>
                    </div>
                  ))}

                  <div style={{ height: 1, background: P.divider, margin: '16px 0' }} />

                  <div
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      color: P.textMuted,
                      fontWeight: 600,
                      marginBottom: 10,
                    }}
                  >
                    Electronic signature
                  </div>
                  <div
                    style={{
                      background: P.pageBg,
                      border: `1px solid ${P.cardBorder}`,
                      borderRadius: 9,
                      padding: '14px 14px 12px',
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: SERIF,
                        fontSize: 22,
                        color: P.textPrimary,
                        borderBottom: `1px solid ${P.accentNeutral}`,
                        paddingBottom: 8,
                      }}
                    >
                      Priya Nair
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                      <span style={{ fontSize: 11, color: P.textMuted, fontFamily: MONO }}>CPA · Lic. TX-0429183</span>
                      <span style={{ fontSize: 11, color: P.textMuted, fontFamily: MONO }}>Jun 25, 2026</span>
                    </div>
                  </div>

                  <button
                    style={{
                      width: '100%',
                      background: P.orange,
                      color: P.onOrange,
                      border: 'none',
                      borderRadius: 10,
                      padding: 13,
                      fontSize: 14.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: BODY,
                      marginTop: 8,
                    }}
                  >
                    Sign off &amp; finalize
                  </button>
                  <button
                    style={{
                      width: '100%',
                      background: 'transparent',
                      color: P.textLabel,
                      border: `1px solid ${P.controlBorder}`,
                      borderRadius: 10,
                      padding: 11,
                      fontSize: 13.5,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: BODY,
                      marginTop: 8,
                    }}
                  >
                    Return with comments
                  </button>
                  <p style={{ fontSize: 11, color: P.textMuted, lineHeight: 1.5, margin: '12px 0 0', textAlign: 'center' }}>
                    Signing writes a tamper-evident entry to the case audit chain.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
